import { Observable, of, throwError } from 'rxjs';
import { defaultIfEmpty, map, switchMap } from 'rxjs/operators';

interface IResolveOrganizationFetchTargetParams {
	preferredOrganizationId?: number;
	switchTargetId?: number;
	selectedOrganizationId?: number;
}

interface IShouldLoadResolvedOrganizationPermissionsParams {
	didFetch: boolean;
	canCommit: boolean;
}

export interface IClientOrganizationResolveResult<T> {
	organization: T;
	didFetch: boolean;
}

/**
 * Returns whether a fetched organization may write `currentOrganization`.
 * A newer in-flight switch target blocks stale bootstrap / `/client` resolver writes
 * and must also suppress the observable emission so permissions are not loaded for it.
 */
export function shouldCommitOrganizationSwitch(
	fetchedOrganizationId: number,
	activeSwitchTargetId: number | undefined
): boolean {
	return activeSwitchTargetId == null || fetchedOrganizationId === activeSwitchTargetId;
}

/**
 * Picks which organization bootstrap should request. An explicit preferred id wins,
 * then the in-flight switch pin, then the last committed `selected_org_id`.
 */
export function resolveOrganizationFetchTarget({
	preferredOrganizationId,
	switchTargetId,
	selectedOrganizationId,
}: IResolveOrganizationFetchTargetParams): number | undefined {
	if (preferredOrganizationId != null && !Number.isNaN(preferredOrganizationId)) {
		return preferredOrganizationId;
	}

	if (switchTargetId != null) {
		return switchTargetId;
	}

	if (selectedOrganizationId != null && !Number.isNaN(selectedOrganizationId)) {
		return selectedOrganizationId;
	}

	return undefined;
}

/**
 * Angular Resolve must emit even when a stale bootstrap is suppressed.
 * Use the committed current organization so `/client` navigation can finish.
 */
export function fallbackResolvedOrganization<T>(
	fetchedOrganization: T | null | undefined,
	currentOrganization: T | null | undefined
): T | undefined {
	return fetchedOrganization ?? currentOrganization ?? undefined;
}

/**
 * `/client` must not load Angular permissions for a suppressed fetch.
 * The fallback org is only there so navigation can finish; a newer switch owns grants.
 */
export function shouldLoadResolvedOrganizationPermissions({
	didFetch,
	canCommit,
}: IShouldLoadResolvedOrganizationPermissionsParams): boolean {
	return didFetch && canCommit;
}

/**
 * Completes a `/client` organization resolve from a fetch that may finish without emitting.
 * `didFetch` is false when the bootstrap was suppressed, so callers can skip permissions.
 */
export function completeClientOrganizationResolve<T>(
	fetch$: Observable<T>,
	getCurrentOrganization: () => T | null | undefined
): Observable<IClientOrganizationResolveResult<T>> {
	return fetch$.pipe(
		defaultIfEmpty(null),
		map(fetchedOrganization => {
			const didFetch = fetchedOrganization != null;
			return {
				didFetch,
				organization: fallbackResolvedOrganization(fetchedOrganization, getCurrentOrganization()),
			};
		}),
		switchMap(({ didFetch, organization }) => {
			if (!organization) {
				return throwError(new Error('No organization was found'));
			}

			return of({ didFetch, organization });
		})
	);
}
